import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (i18n.language === 'fa') {
      document.body.classList.add('font-yekan');
    } else {
      document.body.classList.remove('font-yekan');
    }
  }, [i18n.language]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const navigationItems = [
    { label: t('nav.home'), path: '/landing-page', icon: 'Home' },
    { label: t('nav.api_docs'), path: '/api-documentation', icon: 'FileText' },
    { label: t('nav.pricing'), path: '/pricing-plans', icon: 'DollarSign' },
    { label: t('nav.contact_sales'), path: '/contact-sales', icon: 'MessageSquare' }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const getContextualCTA = () => {
    switch (location?.pathname) {
      case '/landing-page':
        return { text: t('cta.get_started'), variant: 'default', icon: 'ArrowRight' };
      case '/api-documentation':
        return { text: t('cta.try_api'), variant: 'default', icon: 'Code' };
      case '/pricing-plans':
        return { text: t('cta.start_free_trial'), variant: 'default', icon: 'Zap' };
      case '/contact-sales':
        return { text: t('cta.schedule_demo'), variant: 'default', icon: 'Calendar' };
      default:
        return { text: t('cta.get_started'), variant: 'default', icon: 'ArrowRight' };
    }
  };

  const contextualCTA = getContextualCTA();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ease-out ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-sm border-b border-border shadow-elevation-2' 
          : 'bg-background/80 backdrop-blur-sm'
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          {/* Logo */}
          <div className="flex items-center">
            <a 
              href="/landing-page" 
              className="flex items-center space-x-3 transition-opacity duration-200 ease-out hover:opacity-80"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                <Icon name="MessageCircle" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">
                MessagingHub Pro
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems?.map((item) => (
              <a
                key={item?.path}
                href={item?.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ease-out ${
                  isActivePath(item?.path)
                    ? 'text-primary bg-primary/10 border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </a>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="relative">
              <select
                onChange={(e) => changeLanguage(e.target.value)}
                defaultValue={i18n.language}
                className="bg-transparent text-muted-foreground hover:text-foreground focus:outline-none"
              >
                <option value="en">English</option>
                <option value="fa">فارسی</option>
              </select>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="LogIn"
              iconPosition="left"
              onClick={() => window.location.href = '/developer-portal'}
              className="text-muted-foreground hover:text-foreground"
            >
              {t('nav.developer_portal')}
            </Button>
            <Button
              variant={contextualCTA?.variant}
              size="sm"
              iconName={contextualCTA?.icon}
              iconPosition="right"
              className="shadow-elevation-1 hover:shadow-elevation-2"
            >
              {contextualCTA?.text}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden animate-slide-down">
            <div className="px-6 py-4 bg-card/95 backdrop-blur-sm border-t border-border">
              <nav className="space-y-2">
                {navigationItems?.map((item) => (
                  <a
                    key={item?.path}
                    href={item?.path}
                    onClick={closeMobileMenu}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ease-out ${
                      isActivePath(item?.path)
                        ? 'text-primary bg-primary/10 border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>
                  </a>
                ))}
              </nav>
              
              <div className="mt-6 pt-4 border-t border-border space-y-3">
                <div className="relative">
                  <select
                    onChange={(e) => changeLanguage(e.target.value)}
                    defaultValue={i18n.language}
                    className="bg-transparent text-muted-foreground hover:text-foreground focus:outline-none w-full"
                  >
                    <option value="en">English</option>
                    <option value="fa">فارسی</option>
                  </select>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="LogIn"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    closeMobileMenu();
                    window.location.href = '/developer-portal';
                  }}
                  className="justify-start text-muted-foreground hover:text-foreground"
                >
                  {t('nav.developer_portal')}
                </Button>
                <Button
                  variant={contextualCTA?.variant}
                  size="sm"
                  iconName={contextualCTA?.icon}
                  iconPosition="right"
                  fullWidth
                  onClick={closeMobileMenu}
                  className="shadow-elevation-1"
                >
                  {contextualCTA?.text}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;